import * as React from 'react';
import { DatePickerRange, Form, CustomSubmitButton, Select, ClearFormButton, CustomSubmitButtonType } from "soprasteria-ui-library";
import { CareProviderDropdown } from "../../CareProviderDropdown/CareProviderDropdown";
import { IListItem } from 'soprasteria-ui-library/dist/models/IFormInput';
import { OrthodonticLkgCfPatientSearchApi } from '../../../api/OrthodonticLkgCfPatientSearchApi';
import { useAuth } from '../../../App';
import { IOrthodonticLkgCfPatientSearchParams } from '../../../models/IOrthodonticLkgCfPatient';
import { ICareProvider } from '../../../models/ICareProvider';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileExcel } from '@fortawesome/free-solid-svg-icons';
import { isInRole, UserContext } from '../../../contexts/UserContext';
import { LIBRETTOADMIN, VÅRDANSVARIG } from '../../../utils/RoleConstants';

interface IOrthodonticLkgCfPatientsSearchForm {
    preSetSearchParams?: IOrthodonticLkgCfPatientSearchParams;
    searchOrthodonticLkgCfPatients: (data: IOrthodonticLkgCfPatientSearchParams) => void;
    isParaply: boolean;
    isLoading: (isLoading: boolean) => void;
    pageSize: number;
}

export const OrthodonticLkgCfPatientsSearchForm: React.FunctionComponent<IOrthodonticLkgCfPatientsSearchForm> = ({ searchOrthodonticLkgCfPatients, preSetSearchParams, isParaply, isLoading, pageSize }) => {
    const auth = useAuth();
    const api = new OrthodonticLkgCfPatientSearchApi(auth);
    const [orthodonticLkgCfPatientStatuses, setOrthodonticLkgCfPatientStatuses] = React.useState<IListItem[]>()
    const [orthodonticLkgCfPatientTypes, setOrthodonticLkgCfPatientTypes] = React.useState<IListItem[]>()
    const [orthodonticLkgCfPatientAgeIntervals, setOrthodonticLkgCfPatientAgeIntervals] = React.useState<IListItem[]>()
    const [isFetchingUmbrellaOrganisations, setIsFetchingUmbrellaOrganisations] = React.useState<boolean>(false);
    const [isFetchingCareProviders, setIsFetchingCareProviders] = React.useState<boolean>(false);
    const [umbrellaOrganisations, setUmbrellaOrganisations] = React.useState<ICareProvider[]>([]);
    const [careProviders, setCareProviders] = React.useState<ICareProvider[]>();
    const [selectedUmbrellaOrganisationCareProviderId, setSelectedUmbrellaOrganisationCareProviderId] = React.useState<number>();
    const [resetCareProviderValue, doResetCareProviderValue] = React.useState<boolean>(false);
    const [isExportingToExcel, setIsExportingToExcel] = React.useState<boolean>(false);
    const userInfo = React.useContext(UserContext);
    const hasRoleSystemAdministrator = isInRole(LIBRETTOADMIN, userInfo.roles);
    const hasRoleParaply = isInRole(VÅRDANSVARIG, userInfo.roles);

    const abortController = new AbortController();
    React.useEffect(() => {
        getUmbrellaOrganisations();
        getOrthodonticLkgCfPatientStatuses();
        getOrthodonticLkgCfPatientTypes();
        getOrthodonticLkgCfPatientAgeIntervals();

        return () => {
            abortController.abort();
        }
    }, [])

    React.useEffect(() => {
        isLoading(isFetchingCareProviders || isFetchingUmbrellaOrganisations);
    }, [isFetchingCareProviders, isFetchingUmbrellaOrganisations])

    const getUmbrellaOrganisations = () => {
        setIsFetchingUmbrellaOrganisations(true);
        api.getUmbrellaOrganisations((umbrellaOrganisations) => {
            let umbrellaOrganisationCareProviderIdToUse;
            if (preSetSearchParams?.umbrellaOrganisationCareProviderId) {
                umbrellaOrganisationCareProviderIdToUse = parseInt(preSetSearchParams.umbrellaOrganisationCareProviderId.toString());
                setSelectedUmbrellaOrganisationCareProviderId(umbrellaOrganisationCareProviderIdToUse)
                getCareProvidersByUmbrellaOrganisationCareProviderId(umbrellaOrganisationCareProviderIdToUse);
            }
            else if (!hasRoleSystemAdministrator) {
                umbrellaOrganisationCareProviderIdToUse = umbrellaOrganisations[0].careProviderId;
                setSelectedUmbrellaOrganisationCareProviderId(umbrellaOrganisationCareProviderIdToUse);
                getCareProvidersByUmbrellaOrganisationCareProviderId(umbrellaOrganisationCareProviderIdToUse, umbrellaOrganisations);
            }

            setUmbrellaOrganisations(umbrellaOrganisations);
        }, undefined, () => {
            setIsFetchingUmbrellaOrganisations(false);
        });
    }

    const getCareProvidersByUmbrellaOrganisationCareProviderId = (umbrellaOrganisationCareProviderId: number, umbrellaOrganisationsAlt?: ICareProvider[]) => {
        setIsFetchingCareProviders(true);

        api.getCareProvidersByUmbrellaOrganisationCareProviderId(umbrellaOrganisationCareProviderId, (careProviders) => {
            if (careProviders.length !== 0) {
                setCareProviders(careProviders);
            } else {
                umbrellaOrganisationCareProviderId = parseInt(umbrellaOrganisationCareProviderId.toString());
                let umbrellaOrgnisation = umbrellaOrganisationsAlt !== undefined
                    ? umbrellaOrganisationsAlt.find(c => c.careProviderId === umbrellaOrganisationCareProviderId)
                    : umbrellaOrganisations.find(c => c.careProviderId === umbrellaOrganisationCareProviderId);
                setCareProviders([umbrellaOrgnisation!]);
            }
            setIsFetchingCareProviders(false);
        }, undefined, () => { });
    }

    const onUmbrellaOrganisationChange = (selectedCareProvider: string | undefined) => {
        if (hasRoleSystemAdministrator) {
            if (selectedCareProvider && selectedCareProvider !== "") {
                getCareProvidersByUmbrellaOrganisationCareProviderId(parseInt(selectedCareProvider));
                setSelectedUmbrellaOrganisationCareProviderId(parseInt(selectedCareProvider));
            }
            else {
                setSelectedUmbrellaOrganisationCareProviderId(undefined);
                setCareProviders([]);
            }
            doResetCareProviderValue(prev => !prev);
        }
    }

    const getUmbrellaOrganisationDefaultValue = () => {
        if (umbrellaOrganisations && preSetSearchParams?.umbrellaOrganisationCareProviderId) {
            return preSetSearchParams.umbrellaOrganisationCareProviderId.toString();
        }

        if (selectedUmbrellaOrganisationCareProviderId) {
            return selectedUmbrellaOrganisationCareProviderId.toString();
        }

        if (!hasRoleSystemAdministrator && umbrellaOrganisations?.length === 1) {
            return umbrellaOrganisations[0].careProviderId.toString();
        }

        return undefined;
    }

    const getCareProviderDefaultValue = () => {
        if (careProviders) {
            const preSetCareProviderIdCasted = preSetSearchParams?.careProviderId
                ? parseInt(preSetSearchParams.careProviderId.toString())
                : undefined;

            const preSetCareProviderIdExistInCareProviders = preSetCareProviderIdCasted !== undefined
                ? careProviders.some((careProvider) => careProvider.careProviderId === preSetCareProviderIdCasted)
                : undefined;

            if (careProviders && preSetCareProviderIdExistInCareProviders) {
                return preSetSearchParams?.careProviderId?.toString();
            }

            if (careProviders && careProviders.length === 1 && careProviders[0] !== undefined) {
                return careProviders[0].careProviderId.toString();
            }
        }

        return undefined;
    }

    const getOrthodonticLkgCfPatientStatuses = () => {
        api.getOrthodonticLkgCfPatientStatuses((statuses) => {
            const statusListItems = statuses.map<IListItem>((status) => ({ text: status.description, value: status.statusId.toString() }));
            setOrthodonticLkgCfPatientStatuses(statusListItems);
        });
    }

    const getOrthodonticLkgCfPatientTypes = () => {
        api.getOrthodonticLkgCfPatientTypes((patientTypes) => {
            const patientTypeListItems = patientTypes.map<IListItem>((patientType) => ({ text: patientType.description, value: patientType.patientTypeId.toString() }));
            setOrthodonticLkgCfPatientTypes(patientTypeListItems);
        });
    }

    const getOrthodonticLkgCfPatientAgeIntervals = () => {
        api.getOrthodonticLkgCfPatientAgeIntervals((ageIntervals) => {
            const ageIntervalListItems = ageIntervals.map<IListItem>((ageInterval) => ({ text: ageInterval.description, value: ageInterval.ageIntervalId.toString() }));
            setOrthodonticLkgCfPatientAgeIntervals(ageIntervalListItems);
        });
    }

    const exportSearchResultToExcel = (data: IOrthodonticLkgCfPatientSearchParams) => {
        setIsExportingToExcel(true);
        api.searchOrthodonticLkgCfPatientsAsExcel(data, "Registrerade patienter", undefined, undefined, () => setIsExportingToExcel(false));
    }

    return (
        <section>
            <Form>
                <div className="row">
                    <div className="col-6">
                        <Select name="orthodonticLkgCfPatientTypeId" label="Typ av patient" placeholder="Välj en patienttyp" inlineLabel isLoading={orthodonticLkgCfPatientTypes === undefined} selectedValue={preSetSearchParams?.orthodonticLkgCfPatientTypeId?.toString()} options={orthodonticLkgCfPatientTypes ?? []} isClearable />
                        <Select name="orthodonticLkgCfPatientStatusId" label="Status" placeholder="Välj en status" inlineLabel isLoading={orthodonticLkgCfPatientStatuses === undefined} selectedValue={preSetSearchParams?.orthodonticLkgCfPatientStatusId?.toString()} options={orthodonticLkgCfPatientStatuses ?? []} isClearable />
                        <CareProviderDropdown
                            name="umbrellaOrganisationCareProviderId"
                            label="Vårdgivare"
                            isLoading={isFetchingUmbrellaOrganisations}
                            careProviders={umbrellaOrganisations}
                            onValueChange={onUmbrellaOrganisationChange}
                            defaultValue={getUmbrellaOrganisationDefaultValue()}
                            disabled={!hasRoleSystemAdministrator}
                            useFixedListItemHeight
                            showOrgNum
                            placeholder="Välj vårdgivare"
                            isClearable
                        />
                        <CareProviderDropdown
                            name="careProviderId"
                            label="Mottagning"
                            isLoading={isFetchingCareProviders}
                            careProviders={careProviders ?? []}
                            defaultValue={getCareProviderDefaultValue()}
                            disabled={!(hasRoleParaply || hasRoleSystemAdministrator)}
                            useFixedListItemHeight
                            showClinicId
                            isClearable
                            resetValue={resetCareProviderValue}
                        />
                    </div>
                    <div className="col-6">
                        <Select name="orthodonticLkgCfPatientAgeIntervalId" label="Åldersgrupp" placeholder="Välj en åldersgrupp" inlineLabel isLoading={orthodonticLkgCfPatientAgeIntervals === undefined} selectedValue={preSetSearchParams?.orthodonticLkgCfPatientAgeIntervalId?.toString()} options={orthodonticLkgCfPatientAgeIntervals ?? []} isClearable />
                        <DatePickerRange label="Startdatum från/till" name="lkgCfStartedDateFrom" nameSecondary="lkgCfStartedDateTo" inlineLabel
                            value={preSetSearchParams?.lkgCfStartedDateFrom ? new Date(preSetSearchParams.lkgCfStartedDateFrom) : undefined}
                            valueSecondary={preSetSearchParams?.lkgCfStartedDateTo ? new Date(preSetSearchParams.lkgCfStartedDateTo) : undefined} />
                    </div>
                    <div className="col-12">
                        <div className="text-right panel">
                            <span className="d-none">
                                <CustomSubmitButton onButtonSubmit={searchOrthodonticLkgCfPatients} >Sök</CustomSubmitButton>
                            </span>
                            <CustomSubmitButton buttonType={CustomSubmitButtonType.alternative} onButtonSubmit={exportSearchResultToExcel} isLoading={isExportingToExcel}><FontAwesomeIcon icon={faFileExcel} /> Exportera till excel</CustomSubmitButton>
                            <ClearFormButton />
                            <CustomSubmitButton id="submitbutton" onButtonSubmit={searchOrthodonticLkgCfPatients}>Sök</CustomSubmitButton>
                        </div>
                    </div>
                </div>
            </Form>
        </section>
    )
}